/**
 * AreaFormField — the "which instruction sphere does this belong to" control
 * shared by every create/edit form in the work-security module (PR-2 §P4:
 * role, group, training test, incident). Two states only:
 *
 *   - A concrete sphere is in play (the module header is scoped to one, or —
 *     for editing an existing item — that item's own sphere): a read-only
 *     "Sritis: <name>" line. This is the "protection from filing into the
 *     wrong sphere" the spec calls out — you cannot accidentally retarget it.
 *   - "All spheres" mode: a required Polaris `Select` with no empty option,
 *     forcing an explicit choice instead of silently falling back to a
 *     default the user never saw.
 *
 * With one or zero accessible spheres (today's reality for every company
 * except the one piloting a second sphere) this renders nothing at all, so
 * the form is byte-for-byte what it was before spheres existed.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "@shopify/polaris";
import { WorkSecurityArea } from "services/work-security-areas.service";

export interface AreaFormFieldProps {
  /** Candidate spheres for the Select (already filtered by the caller — e.g.
   *  the incident form only offers spheres with `hasIncidents`). */
  areas: WorkSecurityArea[];
  /** A concrete sphere to show read-only; null/undefined renders the Select. */
  selectedArea?: WorkSecurityArea | null;
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

/** System sphere, else the first by sort order — same default rule as
 *  useAreaScope's own resolution, used here to pre-fill the Select. */
export function defaultAreaSelection(areas: WorkSecurityArea[]): string {
  const system = areas.find((area) => area.isSystem);
  return system ? system.id : areas[0]?.id || "";
}

export default function AreaFormField({
  areas,
  selectedArea,
  value,
  onChange,
  disabled,
}: AreaFormFieldProps) {
  const { t } = useTranslation();

  if (areas.length <= 1) {
    return null;
  }

  if (selectedArea) {
    return (
      <div className="text-sm">
        <span className="text-zinc-500">
          {t("area_switcher_label", { defaultValue: "Sritis" })}:
        </span>{" "}
        <span className="font-medium text-zinc-900">{selectedArea.name}</span>
      </div>
    );
  }

  return (
    <Select
      label={t("area_switcher_label", { defaultValue: "Sritis" })}
      options={areas.map((area) => ({ label: area.name, value: area.id }))}
      value={value}
      onChange={onChange}
      disabled={disabled}
      requiredIndicator
    />
  );
}
